/**
 * AcquisitionPage — live data only. /api/v1/dashboard/acquisition.
 *
 * Sections, all live:
 *   1. Signups (today / week / month) — anchored to fixed periods regardless
 *      of date pill, because the labels say so.
 *   2. 12-month signup trend (label uses 'MMM YY' so years don't collide).
 *   3. Referral funnel — split by FLOW per the PRD with each flow's trigger
 *      gate (Creator→Fan + Fan→Fan: signup completion; Creator→Creator: KYC
 *      verification).
 *   4. Creator activation pipeline — KYC stages between signup and "can take
 *      a paid call". Uses `is_verify` (creator KYC), not `is_verified` (brand).
 *   5. Channel mix — Branch.io install attribution + Meta Ads spend + GA4
 *      acquisition channels. Default window for these is last-30d when the
 *      date pill is on Till-date (external APIs are rate-limited).
 *   6. Cost per acquired — Meta CPI/CPA + Referral CAC (with sample-size
 *      guard — won't divide if < 50 eligible referrals in window) + GA4
 *      organic share.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { PageGuide } from "./PageGuide.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { useDashboardPage, formatNum, formatPct, formatINR } from "./useDashboardPage.v2";
import { rangeWordFor, granLabel as glab } from "./rangeWord.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

export function AcquisitionPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("acquisition", range, rangeId);
  if (loading) return <LoadingShell tiles={4} />;
  if (!data) return <EmptyError title="Couldn't load Acquisition" error={error} onRetry={reload} />;

  const h = data.hero || {};
  const rf = data.referralFunnel || {};
  const flows = data.referralFlows || {};
  const pipe = data.creatorPipeline || {};
  const isTillDate = data.range?.isTillDate;
  const word = rangeWordFor(rangeId);
  const windowWord = word; // alias

  // Eligibility = signups completed registration (the trigger gate that
  // unlocks both-side payouts). Only meaningful denominators are used.
  const eligibilityPct = rf.totalReferrals > 0
    ? (rf.eligibleForClaim / rf.totalReferrals) * 100 : 0;

  const sw = h.signupsInWindow || {};
  const st = h.signupsToday    || {};
  const deltaPct = sw.deltaPct;
  const deltaText = deltaPct == null ? null : `${deltaPct >= 0 ? '+' : ''}${deltaPct}% vs prev`;

  const heroTiles = [
    {
      label: `Signups · ${windowWord}`, icon: "S", variant: "muted-orange",
      value: formatNum(sw.value),
      subValue: `${formatNum(sw.fans)} fans · ${formatNum(sw.creators)} creators${deltaText ? ` · ${deltaText}` : ''}`,
      title:
        `New users created in the selected date window.\n` +
        `  Total:    ${formatNum(sw.value)}\n` +
        `  Fans:     ${formatNum(sw.fans)}\n` +
        `  Creators: ${formatNum(sw.creators)}\n` +
        (deltaText ? `\nvs previous period of equal length: ${deltaText}` : ''),
    },
    {
      label: "Signups today", icon: "T", variant: "muted-violet",
      value: formatNum(st.value),
      subValue: `${formatNum(st.fans)} fans · ${formatNum(st.creators)} creators`,
      title: `New users created today (literal today). Anchored — does not react to the date pill.`,
    },
    {
      label: `Total referrals · ${windowWord}`, icon: "R", variant: "muted-lime",
      value: formatNum(rf.totalReferrals),
      subValue: `${formatNum(rf.eligibleForClaim)} eligible (${formatPct(eligibilityPct, 1)})`,
      title:
        `Referral invites created in the selected window.\n` +
        `  Total invites:    ${formatNum(rf.totalReferrals)}\n` +
        `  Signup completed: ${formatNum(rf.eligibleForClaim)}\n` +
        `Eligibility rate gates payout — both inviter and signup are paid\n` +
        `only after this gate clears.`,
    },
    {
      label: "Eligibility rate", icon: "E", variant: "muted-magenta",
      value: formatPct(eligibilityPct, 1),
      subValue: `${formatNum(rf.eligibleForClaim)} of ${formatNum(rf.totalReferrals)} · ${word}`,
      title:
        `Trigger gate: % of referral pairs where the new signup actually\n` +
        `completed registration. Both-side rewards only fire after this\n` +
        `gate clears.`,
    },
  ];

  const trend = data.trend?.signupsLast12Months || [];

  // Per-flow tiles — each flow has its own gate per the PRD.
  const cToF = flows.creatorToFan || {};
  const fToF = flows.fanToFan || {};
  const cToC = flows.creatorToCreator || {};

  const flowTiles = [
    {
      label: "Creator → Fan", icon: "→", variant: "muted-orange",
      value: `${formatNum(cToF.bothSidesPaid)} / ${formatNum(cToF.invited)}`,
      subValue: `${formatNum(cToF.signupCompleted)} signups completed`,
      title:
        `Creators inviting fans. Reward (₹50 to fan + ₹50 to creator's\n` +
        `referral pot) fires only after the fan completes registration.\n` +
        `\n` +
        `  Invited:           ${formatNum(cToF.invited)}\n` +
        `  Signup completed:  ${formatNum(cToF.signupCompleted)}\n` +
        `  Both sides paid:   ${formatNum(cToF.bothSidesPaid)}\n` +
        `\n` +
        `Largest flow on the platform — primary creator acquisition lever.`,
    },
    {
      label: "Fan → Fan", icon: "→", variant: "muted-violet",
      value: `${formatNum(fToF.bothSidesPaid)} / ${formatNum(fToF.invited)}`,
      subValue: `${formatNum(fToF.signupCompleted)} signups completed`,
      title:
        `Fans inviting fans. Reward sliding ₹50–₹1,000 (sliced based on\n` +
        `referee's first wallet credit) credits to the inviter's regular\n` +
        `wallet, NOT the referral pot.\n` +
        `\n` +
        `  Invited:           ${formatNum(fToF.invited)}\n` +
        `  Signup completed:  ${formatNum(fToF.signupCompleted)}\n` +
        `  Both sides paid:   ${formatNum(fToF.bothSidesPaid)}\n` +
        `\n` +
        `Conversion is much lower than Creator→Fan (creators have stronger\n` +
        `incentive to push their code).`,
    },
    {
      label: "Creator → Creator", icon: "→", variant: "muted-lime",
      value: `${formatNum(cToC.verifiedAndPaid)} / ${formatNum(cToC.invited)}`,
      subValue: `${formatNum(cToC.pending)} pending verification`,
      title:
        `Creators inviting other creators. Two-stage payout:\n` +
        `  • ₹250 on signup completion\n` +
        `  • ₹750 only after the new creator's KYC clears verification\n` +
        `\n` +
        `  Invited:               ${formatNum(cToC.invited)}\n` +
        `  Verified & both paid:  ${formatNum(cToC.verifiedAndPaid)}\n` +
        `  Pending verification:  ${formatNum(cToC.pending)}\n` +
        `\n` +
        `Conversion is intentionally tight — verification gate is strict.`,
    },
  ];

  // Creator pipeline — built around the trigger gate (active + verified
  // can take a paid call). The raw `kycApproved` and `kycNotStarted` buckets
  // from getCreatorKycStatus overlap (auto-approval flags is_verify=true
  // even for creators who never uploaded bank docs), so we don't surface
  // them side-by-side. Instead we frame around three honest, non-overlapping
  // states: callable, signed-up-only, rejected.
  const verifiedInactive = Math.max(0, (pipe.kycApproved || 0) - (pipe.activeVerified || 0));
  const callablePct = pipe.total > 0 ? (pipe.activeVerified / pipe.total) * 100 : 0;
  const noDocsPct   = pipe.total > 0 ? (pipe.kycNotStarted / pipe.total) * 100 : 0;
  const pipelineTiles = [
    {
      label: "Total creators", icon: "T", variant: "muted-orange",
      value: formatNum(pipe.total),
      subValue: `${formatNum(pipe.activeVerified)} can currently take calls`,
      title:
        `Lifetime count of role_id=3 (creator) accounts. The dashboard-wide\n` +
        `trigger-gating rule means only "active + verified" creators can\n` +
        `actually take a paid call — that's the capacity number to track.`,
    },
    {
      label: "Active + verified", icon: "✓", variant: "muted-electric",
      value: formatNum(pipe.activeVerified),
      subValue: `${formatPct(callablePct, 1)} of all creators`,
      title:
        `Creators who can currently take a paid call:\n` +
        `is_active = true AND is_verify = true. This is the trigger-gated\n` +
        `"active creator" count — the only KPI that matters for capacity\n` +
        `planning.`,
    },
    {
      label: "Verified but inactive", icon: "P", variant: "muted-yellow",
      value: formatNum(verifiedInactive),
      subValue: "passed KYC but is_active = false",
      title:
        `Creators who completed KYC but their account is currently inactive\n` +
        `(self-paused, suspended, or never finished onboarding). Could\n` +
        `become callable again with re-engagement.`,
    },
    {
      label: "No bank docs", icon: "○", variant: "muted-violet",
      value: formatNum(pipe.kycNotStarted),
      subValue: `${formatPct(noDocsPct, 1)} of total · payout blocked`,
      title:
        `Creators who never uploaded bank details (celebrity_bank_details\n` +
        `row missing). Cannot receive payouts even if they take calls.\n` +
        `Note: this bucket OVERLAPS with "active + verified" because the\n` +
        `platform auto-approves is_verify on signup for some accounts —\n` +
        `i.e. some are technically callable but unbanked.`,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary }}>Acquisition</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Signups · referral funnel · creator activation pipeline · live from production</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {heroTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {trend.length > 0 && (() => {
        const gran = data.range?.granularity || 'month';
        const granLabel = glab(gran);
        const granNoun  = ({ hour: 'hour', day: 'day', week: 'week', month: 'month', year: 'year' })[gran] || 'month';
        const titleSuffix = isTillDate ? 'last 12 months' : word;
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, padding: "0 4px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Signups per {granNoun} — {titleSuffix}</div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
            </div>
            <AreaChart data={trend} format={formatNum} summaryLabel={trend[trend.length - 1]?.label} summaryValue={formatNum(trend[trend.length - 1]?.value)} summaryDelta="" />
          </div>
        );
      })()}

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Referral funnel · per flow · {word}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {flowTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Creator activation pipeline · lifetime</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {pipelineTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {(() => {
        // Channel attribution — wired to Branch.io + Meta Ads + GA4. Window
        // matches the date pill (default last-30d when on Till-date because
        // external API rate limits make lifetime channel mix impractical).
        const cm = data.channelMix || {};
        const cw = data.source?.channelWindow || {};
        const cacD = data.cac || {};
        const channelWindowLabel = cw.from && cw.to
          ? `${cw.from} → ${cw.to}`
          : "last 30 days";

        const branchTiles = cm.branch ? [
          {
            label: "Total app installs", icon: "I", variant: "muted-orange",
            value: formatNum(cm.branch.totalInstalls),
            subValue: `Branch.io · ${channelWindowLabel}`,
            title:
              `Total installs Branch attributed in this window (Android + iOS).\n` +
              `Authoritative install count — Branch fingerprints every install\n` +
              `and tags it with the touch source. Numbers may exceed signup\n` +
              `counts because not every install completes registration.`,
          },
          {
            label: "Paid (Meta/FB)", icon: "P", variant: "muted-magenta",
            value: formatNum(cm.branch.paidInstalls),
            subValue: cm.branch.totalInstalls > 0
              ? `${formatPct((cm.branch.paidInstalls / cm.branch.totalInstalls) * 100, 1)} of installs`
              : "—",
            title:
              `Branch-attributed paid installs — currently 100% Facebook/Meta\n` +
              `(Instagram rolls up under the same channel name in Branch).\n` +
              `Aligns with Meta's own appInstalls but Branch is the source\n` +
              `of truth for platform-side attribution.`,
          },
          {
            label: "Organic / direct", icon: "O", variant: "muted-lime",
            value: formatNum(cm.branch.unattributedInstalls),
            subValue: cm.branch.totalInstalls > 0
              ? `${formatPct((cm.branch.unattributedInstalls / cm.branch.totalInstalls) * 100, 1)} of installs`
              : "—",
            title:
              `Branch couldn't attribute these to a paid touch — they came\n` +
              `from Play Store / App Store search, direct URL, or referral\n` +
              `links that didn't carry a Branch tag. This is the "free"\n` +
              `acquisition share.`,
          },
          {
            label: "Other", icon: "?", variant: "muted-violet",
            value: formatNum(cm.branch.otherInstalls),
            subValue: "test campaigns / unresolved",
            title:
              `Installs Branch tagged with a non-human-readable channel ID\n` +
              `(internal test campaigns, bot traffic, or partial deeplinks).\n` +
              `Should stay small — anything above 5% of total warrants a\n` +
              `Branch dashboard audit.`,
          },
        ] : [];

        const cacTiles = [];
        if (cacD.meta) {
          cacTiles.push({
            label: "Meta CPI", icon: "M", variant: "muted-magenta",
            value: formatINR(cacD.meta.cpi),
            subValue: `${formatINR(cacD.meta.spend)} spend · ${formatNum(cacD.meta.installs)} installs`,
            title:
              `Cost per install on Meta Ads (Facebook + Instagram).\n` +
              `  Spend:        ${formatINR(cacD.meta.spend)}\n` +
              `  Installs:     ${formatNum(cacD.meta.installs)}\n` +
              `  Activations:  ${formatNum(cacD.meta.activations)}\n` +
              `  CPI:          ${formatINR(cacD.meta.cpi)}\n` +
              `  CPA:          ${cacD.meta.cpa ? formatINR(cacD.meta.cpa) : "—"}\n` +
              `\n` +
              `CPA = cost per first-app-open (a stronger signal than CPI\n` +
              `because it filters out installs that never opened the app).`,
          });
          if (cacD.meta.cpa) {
            cacTiles.push({
              label: "Meta CPA", icon: "A", variant: "muted-orange",
              value: formatINR(cacD.meta.cpa),
              subValue: `${formatNum(cacD.meta.activations)} first-app-opens`,
              title:
                `Cost per app activation (first open after install).\n` +
                `Always lower than CPI when activations > installs — Meta\n` +
                `attributes some opens that pre-date the install record\n` +
                `(re-installs, lapsed users returning).`,
            });
          }
        }
        if (cacD.referral) {
          const r = cacD.referral;
          const cacValue = r.cac != null ? formatINR(r.cac) : "—";
          const cacSub = r.cac != null
            ? `${formatINR(r.totalPaid)} paid · ${formatNum(r.eligibleReferrals)} eligible`
            : `${formatNum(r.eligibleReferrals)} eligible (need ≥${r.minSample} for CAC)`;
          cacTiles.push({
            label: "Referral CAC", icon: "R", variant: "muted-lime",
            value: cacValue,
            subValue: cacSub,
            title:
              `Cost per eligible referral in this window.\n` +
              `  Total paid (window):     ${formatINR(r.totalPaid)}\n` +
              `  Payouts (window):        ${formatNum(r.payouts)}\n` +
              `  Eligible referrals:      ${formatNum(r.eligibleReferrals)}\n` +
              `\n` +
              (r.cac != null
                ? `CAC = total paid ÷ eligible referrals.`
                : `CAC suppressed — eligibility count below ${r.minSample}.\n` +
                  `In short windows, payouts catch up on referrals from\n` +
                  `prior windows (numerator ≠ denominator population),\n` +
                  `producing a misleading CAC. Switch to a longer pill\n` +
                  `(90d / Till-date) to see a meaningful number.`),
          });
        }
        if (cm.ga4) {
          const orgPct = cm.ga4.totalNewUsers > 0
            ? (cm.ga4.organicNewUsers / cm.ga4.totalNewUsers) * 100 : 0;
          cacTiles.push({
            label: "Organic share (GA4)", icon: "O", variant: "muted-lime",
            value: formatPct(orgPct, 1),
            subValue: `${formatNum(cm.ga4.organicNewUsers)} of ${formatNum(cm.ga4.totalNewUsers)} new users`,
            title:
              `GA4-attributed new users that arrived via Organic Social /\n` +
              `Organic Search / Direct (no paid spend). Includes web visits\n` +
              `that haven't installed the app yet — complements but doesn't\n` +
              `equal Branch's "(unattributed)" install bucket.`,
          });
        }

        return (
          <>
            <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
              Channel mix · {channelWindowLabel}
            </div>
            {branchTiles.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
                {branchTiles.map(t => <Tile key={t.label} {...t} />)}
              </div>
            ) : (
              <div style={{ padding: "10px 14px", marginBottom: 12, background: T.status.warnBg, border: `1px solid ${T.status.warn}`, borderRadius: 8, fontSize: 12 }}>
                Branch.io install attribution not available for this window — check Branch credentials in env.
              </div>
            )}

            {(() => {
              const storeTiles = [];
              if (cm.appstore) {
                storeTiles.push({
                  label: "iOS installs (App Store)", icon: "i", variant: "muted-violet",
                  value: formatNum(cm.appstore.totalUnits),
                  subValue: cm.appstore.totalProceeds > 0
                    ? `Rs ${formatNum(cm.appstore.totalProceeds)} proceeds · App Store Connect`
                    : `App Store Connect · ${cm.appstore.appName || "iOS"}`,
                  title:
                    `App Store Connect Sales & Trends API.\n` +
                    `Total units = paid + free downloads in the window.\n` +
                    `Proceeds = developer revenue (after Apple's 30%/15% cut).`,
                });
              }
              if (cm.playstore) {
                storeTiles.push({
                  label: "Android installs (Play Store)", icon: "a", variant: "muted-mint",
                  value: formatNum(cm.playstore.totalInstalls),
                  subValue: cm.playstore.uninstalls > 0
                    ? `${formatNum(cm.playstore.uninstalls)} uninstalls · net ${formatNum(cm.playstore.netInstalls)}`
                    : `Play Console · ${cm.playstore.appName || "Android"}`,
                  title:
                    `Google Play Console Reporting API + GCS bucket\n` +
                    `pubsite_prod_6874932402858613379.\n` +
                    `Total installs = first-time installs in the window.\n` +
                    `Uninstalls subtract for net.`,
                });
              }
              if (storeTiles.length === 0) return null;
              return (
                <>
                  <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
                    Native app stores · {channelWindowLabel}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
                    {storeTiles.map(t => <Tile key={t.label} {...t} />)}
                  </div>
                </>
              );
            })()}

            <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
              Cost per acquired · {channelWindowLabel}
            </div>
            {cacTiles.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
                {cacTiles.map(t => <Tile key={t.label} {...t} />)}
              </div>
            ) : (
              <div style={{ padding: "10px 14px", marginBottom: 12, fontSize: 12, color: T.text.muted }}>
                CAC tiles unavailable for this window.
              </div>
            )}
          </>
        );
      })()}

      {/* Mixpanel onboarding funnel — Install → Signup → Profile → First Call.
          Hides cleanly if Mixpanel creds missing or rate-limited (60/hr quota). */}
      {data.mixpanelFunnel && Array.isArray(data.mixpanelFunnel.steps) && data.mixpanelFunnel.steps.length > 0 && (() => {
        const cw = data.source?.channelWindow || {};
        const fLabel = cw.from && cw.to ? `${cw.from} → ${cw.to}` : "last 30 days";
        return (
        <>
          <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
            Onboarding funnel (Mixpanel) · {fLabel}
          </div>
          <div style={{
            background: T.surface.card, border: `1px solid ${T.border.subtle}`,
            borderRadius: 12, padding: 16, marginBottom: 12,
          }}>
            {data.mixpanelFunnel.steps.map((step, i) => {
              const prev = i > 0 ? data.mixpanelFunnel.steps[i - 1] : null;
              const stepDrop = prev && prev.count > 0
                ? ((prev.count - step.count) / prev.count) * 100 : 0;
              return (
                <div key={step.name + i} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 0",
                  borderBottom: i < data.mixpanelFunnel.steps.length - 1 ? `1px solid ${T.border.subtle}` : "none",
                }}>
                  <span style={{
                    width: 26, height: 26, borderRadius: 8,
                    background: T.brand.soft, color: T.brand.base,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800,
                  }}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text.primary }}>{step.name}</div>
                    <div style={{ height: 6, marginTop: 6, background: T.surface.canvas, borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ width: Math.max(0, Math.min(100, step.pct || 0)) + "%", height: "100%", background: T.brand.base, borderRadius: 999 }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 110 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums" }}>
                      {formatNum(step.count)}
                    </div>
                    <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>
                      {(step.pct || 0).toFixed(1)}% of step 1
                      {prev && stepDrop > 0 ? ` · −${stepDrop.toFixed(1)}% drop` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{
              marginTop: 12, paddingTop: 10,
              borderTop: `1px solid ${T.border.subtle}`,
              fontSize: 12, color: T.text.secondary,
            }}>
              <strong style={{ color: T.text.primary }}>Overall conversion:</strong>{" "}
              {(data.mixpanelFunnel.overallConversion || 0).toFixed(1)}% (Install → Last step)
            </div>
          </div>
        </>
        );
      })()}
      <PageGuide groups={ACQUISITION_GUIDE} />
    </>
  );
}

// Definitions for every Acquisition-page card. Internal = Postgres; external =
// third-party APIs (each capped at 12s, some need creds/subscriptions).
const ACQUISITION_GUIDE = [
  {
    section: "Signups",
    rows: [
      { card: "Signups · window", means: "New registrations (fans + creators) in the period, with split and change vs the previous period.", rule: "users created in window (completed: fan phone_verified / creator active-or-verify)." },
      { card: "Signups today", means: "Today's registrations (always literal today, ignores the date pill).", rule: "users created today (IST)." },
      { card: "Total referrals / Eligibility rate", means: "Referral invites created in the period; eligible = the referred user completed signup (the payout trigger).", rule: "user_referral, is_eligible_for_claim." },
      { card: "Signups trend", means: "Signups over time (auto granularity).", rule: "users grouped by period." },
    ],
  },
  {
    section: "Referral funnel",
    rows: [
      { card: "Creator→Fan / Fan→Fan / Creator→Creator", means: "Each referral flow: invited → signup completed → both sides paid. Creator→Creator pays in two stages (signup, then KYC).", rule: "user_referral by inviter/joiner role; excludes synthetic (-7) and admin inviters." },
    ],
  },
  {
    section: "Creator activation",
    rows: [
      { card: "Pipeline (Total / Active+verified / Verified-inactive / No bank docs)", means: "The KYC pipeline from signup to 'can take a paid call'.", rule: "users role=creator in window; active+verified = is_active AND is_verify; no-bank = missing celebrity_bank_details." },
    ],
  },
  {
    section: "Channels & cost (external)",
    rows: [
      { card: "Branch installs (by channel)", means: "App installs split by source.", rule: "Branch.io API — CURRENTLY BLOCKED (Data Feeds subscription inactive); returns empty until reactivated." },
      { card: "Meta CPI / CPA", means: "Cost per install / per first-open from Meta Ads.", rule: "Meta Ads API: spend ÷ installs (CPI) or ÷ activations (CPA). Working." },
      { card: "GA4 organic share", means: "Share of new users arriving organically.", rule: "Firebase GA4 channels API." },
      { card: "iOS / Android installs", means: "Store installs (downloads, uninstalls).", rule: "App Store Connect / Google Play (external)." },
      { card: "Referral CAC", means: "Cost per eligible referral (reward payout ÷ completed referrals). Hidden below 50 referrals.", rule: "transaction_history referral rewards ÷ completed referrals." },
      { card: "Onboarding funnel (Mixpanel)", means: "Install → Signup → Profile → First-call drop-off.", rule: "Mixpanel funnel API." },
    ],
  },
];
